/**
 * Staffing tab for v2.
 *
 * Five departments, each headcount x cost per head.
 *
 * Direct Staff is shown FIRST and flagged, because it behaves differently from
 * the other four: its cost flows to COGS, not to Total Staff Expense. That is
 * the template's design and it is deliberate — counting it in both places
 * would double-charge direct labour. A reader who does not know that will
 * otherwise think the staff total is understated.
 */

import { Fragment, useCallback } from 'react';
import { ComputedDataRow } from '../table/DataRow';
import { V2EditableRow } from './V2EditableRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { StaffingDepartmentV2 } from '../../../types/financial-model-v2';
import { V2Sheet } from './V2Table';

const DEPARTMENTS: Array<{
  key: keyof Pick<
    import('../../../types/financial-model-v2').StaffingInputsV2,
    'directStaff' | 'operationsRiskTech' | 'executiveHR' | 'financeAdmin' | 'productSales'
  >;
  label: string;
  note?: string;
}> = [
  { key: 'directStaff', label: 'Direct Staff', note: 'charged to Cost of Sales, not to staff costs' },
  { key: 'operationsRiskTech', label: 'Operations, Risk & Tech' },
  { key: 'executiveHR', label: 'Executive & HR' },
  { key: 'financeAdmin', label: 'Finance & Admin' },
  { key: 'productSales', label: 'Product & Sales' },
];

export function StaffingViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);

  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );

  if (!model) return null;
  const { inputs, computed } = model;

  return (
    <V2Sheet>
      {DEPARTMENTS.map(({ key, label, note }) => {
        const dept = inputs.staffing[key] as StaffingDepartmentV2;
        return (
          // Fragment, not <>, so the key sits on the outermost element as
          // React requires for a mapped list.
          <Fragment key={key}>
            <SectionHeader label={note ? `${label} — ${note}` : label} />
            <V2EditableRow
              label="Headcount"
              data={dept.headcount}
              onCellChange={handleChange(`staffing.${key}.headcount`)}
            />
            <V2EditableRow
              label="Cost per Head"
              data={dept.costPerHead}
              onCellChange={handleChange(`staffing.${key}.costPerHead`)}
            />
            {key === 'directStaff' && (
              <ComputedDataRow
                label="Department Cost → COGS"
                data={computed.staffing.directStaffCost}
              />
            )}
          </Fragment>
        );
      })}

      <SectionHeader label="Other Staff Costs" />
      <V2EditableRow
        label="Travel and Entertaining"
        data={inputs.staffing.travelEntertaining}
        onCellChange={handleChange('staffing.travelEntertaining')}
      />
      <V2EditableRow
        label="Other Staff costs"
        data={inputs.staffing.otherStaffCosts}
        onCellChange={handleChange('staffing.otherStaffCosts')}
      />

      <SectionHeader label="Totals" />
      <SubtotalRow
        label="Total Indirect Staff Cost (excludes Direct Staff)"
        data={computed.staffing.totalIndirectStaffCost}
      />
      <GrandTotalRow
        label="Total Staff Expense → P&L"
        data={computed.staffing.totalStaffExpense}
      />
    </V2Sheet>
  );
}
